import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextArea,
} from "../crm/Form";
import api, { formatApiError } from "../../lib/api";

function fmtCents(cents) {
  if (cents === null || cents === undefined) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

const CADENCE_LABEL = {
  monthly: "/ month",
  quarterly: "/ quarter",
  annual: "/ year",
};

/**
 * Modal form for enrolling a customer in a plan.
 * Pre-fills from props (`customerId`, `propertyId`, `solarSystemId`) where given.
 */
export default function EnrollmentForm({
  open,
  onClose,
  onCreated,
  customerId,
  propertyId,
  solarSystemId,
}) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [amountOverride, setAmountOverride] = useState("");
  const [cadenceOverride, setCadenceOverride] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    api
      .get("/protection-plans/")
      .then((r) => setPlans(r.data || []))
      .catch((e) => setErr(formatApiError(e)));
  }, [open]);

  const selected = plans.find((p) => p.id === planId);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = {
        plan_id: planId,
        customer_id: customerId,
        start_date: startDate,
      };
      if (propertyId) body.property_id = propertyId;
      if (solarSystemId) body.solar_system_id = solarSystemId;
      if (amountOverride !== "") {
        body.amount_cents_override = Math.round(Number(amountOverride) * 100);
      }
      if (cadenceOverride) body.billing_cadence_override = cadenceOverride;
      if (notes) body.notes_internal = notes;
      const { data } = await api.post("/subscriptions/", body);
      toast.success("Enrolled — subscription is in pending status");
      onCreated(data);
      // reset
      setPlanId("");
      setAmountOverride("");
      setCadenceOverride("");
      setNotes("");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enroll in Protection Plan"
      subtitle="Snapshot at sign-up — catalog price changes won't affect this subscription"
      data-testid="enrollment-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <ErrorBanner message={err} />
        <Field label="Plan" required>
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            data-testid="enroll-plan-select"
            required
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          >
            <option value="">Select a plan…</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code}) — {fmtCents(p.default_amount_cents)}{" "}
                {CADENCE_LABEL[p.default_billing_cadence] || ""}
              </option>
            ))}
          </select>
        </Field>

        {selected && selected.included_features?.length > 0 && (
          <div
            data-testid="enroll-plan-features"
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700"
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
              Includes
            </div>
            <ul className="list-disc list-inside space-y-0.5">
              {selected.included_features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date" required>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="enroll-start-date"
              required
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            />
          </Field>
          <Field label="Cadence override" hint="Optional — defaults to plan">
            <select
              value={cadenceOverride}
              onChange={(e) => setCadenceOverride(e.target.value)}
              data-testid="enroll-cadence-override"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            >
              <option value="">Use plan default</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </Field>
        </div>

        <Field label="Price override (USD)" hint="Optional — leave blank for catalog price">
          <input
            type="number"
            step="0.01"
            min="0"
            value={amountOverride}
            onChange={(e) => setAmountOverride(e.target.value)}
            data-testid="enroll-price-override"
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            placeholder={
              selected
                ? (selected.default_amount_cents / 100).toFixed(2)
                : "0.00"
            }
          />
        </Field>

        <Field label="Internal notes" hint="Not visible to techs or owners">
          <TextArea
            value={notes}
            onChange={setNotes}
            rows={3}
            data-testid="enroll-notes"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy || !planId}
            data-testid="enroll-submit"
          >
            {busy ? "Enrolling…" : "Enroll"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
